import { isRecord, requestJson } from "./http.js";

export interface VaultMeta {
  readonly id: string;
  readonly folder: string;
  readonly titleHash: string;
  readonly version: number;
  readonly favorite: boolean;
  readonly updatedAt: number;
  readonly deletedAt: number | null;
}

export interface VaultItemFull extends VaultMeta {
  readonly ciphertextB64: string;
  readonly nonceB64: string;
}

export interface NewVaultItemInput {
  readonly id: string;
  readonly folder: string;
  readonly titleHash: string;
  readonly ciphertextB64: string;
  readonly nonceB64: string;
  readonly favorite: boolean;
}

export interface VaultPatchInput {
  readonly folder?: string | undefined;
  readonly titleHash?: string | undefined;
  readonly ciphertextB64?: string | undefined;
  readonly nonceB64?: string | undefined;
  readonly favorite?: boolean | undefined;
  readonly expectedVersion: number;
}

export function isVaultMeta(value: unknown): value is VaultMeta {
  return isRecord(value) &&
    typeof value["id"] === "string" &&
    typeof value["folder"] === "string" &&
    typeof value["titleHash"] === "string" &&
    typeof value["version"] === "number" &&
    typeof value["favorite"] === "boolean" &&
    typeof value["updatedAt"] === "number" &&
    (typeof value["deletedAt"] === "number" || value["deletedAt"] === null);
}

export function isVaultItemFull(value: unknown): value is VaultItemFull {
  if (!isVaultMeta(value)) {
    return false;
  }
  if (!("ciphertextB64" in value) || !("nonceB64" in value)) {
    return false;
  }
  return typeof value.ciphertextB64 === "string" && typeof value.nonceB64 === "string";
}

function unexpected(): Error {
  return new Error("Unerwartete Antwort vom Server.");
}

function metaFrom(body: unknown): VaultMeta {
  if (!isRecord(body) || !isVaultMeta(body["item"])) {
    throw unexpected();
  }
  return body["item"];
}

function fullFrom(body: unknown): VaultItemFull {
  if (!isRecord(body) || !isVaultItemFull(body["item"])) {
    throw unexpected();
  }
  return body["item"];
}

export async function listItems(includeDeleted: boolean): Promise<VaultMeta[]> {
  const suffix = includeDeleted ? "?includeDeleted=1" : "";
  const body = await requestJson(`/api/v1/vault${suffix}`);
  if (!isRecord(body) || !Array.isArray(body["items"])) {
    throw unexpected();
  }
  const out: VaultMeta[] = [];
  for (const entry of body["items"]) {
    if (!isVaultMeta(entry)) {
      throw unexpected();
    }
    out.push(entry);
  }
  return out;
}

export async function openItem(id: string): Promise<VaultItemFull> {
  return fullFrom(await requestJson(`/api/v1/vault/${id}`));
}

export async function createItem(input: NewVaultItemInput): Promise<VaultMeta> {
  return metaFrom(await requestJson("/api/v1/vault", { method: "POST", body: input }));
}

export async function patchItem(id: string, patch: VaultPatchInput): Promise<VaultItemFull> {
  return fullFrom(await requestJson(`/api/v1/vault/${id}`, { method: "PATCH", body: patch }));
}

export async function deleteItem(id: string): Promise<VaultMeta> {
  return metaFrom(await requestJson(`/api/v1/vault/${id}`, { method: "DELETE" }));
}

export async function restoreItem(id: string): Promise<VaultMeta> {
  return metaFrom(await requestJson(`/api/v1/vault/${id}/restore`, { method: "POST" }));
}
